import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

/**
 * LV-02B Phase 4 · Workstream C — SMS transactional permission control.
 *
 * Minimal additive panel on the ProjectDetail page. Lets the authenticated
 * contractor:
 *   • see the normalized (masked) customer phone stored on the project
 *   • affirm that the customer authorized transactional SMS document delivery
 *   • grant permission
 *   • revoke permission
 *   • see whether the customer has opted out
 *
 * Does NOT authorize marketing SMS. Does NOT expand to multi-customer.
 * Changing the customer phone invalidates the permission because the
 * server keys on (user_id, project_id, channel, purpose, phone_e164).
 */
export default function SmsPermissionCard({ projectId, customerPhone }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [affirmed, setAffirmed] = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/sms-permission`);
      setState(r?.data || null);
    } catch (e) {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
    // Re-load if the raw project phone changes on the parent page — the
    // server invalidates permission by phone-identity when the number
    // is edited, so the panel must reflect that immediately.
  }, [load, customerPhone]);

  const onGrant = async () => {
    if (!affirmed) {
      toast.error("Please affirm the customer authorized transactional SMS delivery.");
      return;
    }
    setPending(true);
    try {
      await api.post(`/projects/${projectId}/sms-permission/grant`,
                      { affirmation: true });
      toast.success("Transactional SMS permission granted.");
      setAffirmed(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.message || detail?.error || "Could not grant permission.");
    } finally {
      setPending(false);
    }
  };

  const onRevoke = async () => {
    setPending(true);
    try {
      await api.post(`/projects/${projectId}/sms-permission/revoke`);
      toast.success("SMS permission revoked.");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail?.error || "Could not revoke permission.");
    } finally {
      setPending(false);
    }
  };

  if (!projectId) return null;

  const status = state?.status || "not_granted";
  const phoneMasked = state?.phone_masked;

  return (
    <div
      data-testid="sms-permission-card"
      className="mt-5 rounded-lg border border-[var(--jc-border)] bg-white p-4"
    >
      <div className="flex items-start gap-3">
        <MessageSquare size={18} className="mt-0.5 text-[var(--jc-text-soft)]" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-medium text-sm">Text-message document delivery</div>
            {loading ? (
              <Loader2 size={14} className="animate-spin text-[var(--jc-text-soft)]" />
            ) : status === "granted" ? (
              <span data-testid="sms-permission-status-granted"
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">
                <ShieldCheck size={12} /> Granted
              </span>
            ) : status === "opted_out" ? (
              <span data-testid="sms-permission-status-opted-out"
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-800 border border-rose-200">
                <ShieldAlert size={12} /> Customer opted out
              </span>
            ) : status === "revoked" ? (
              <span data-testid="sms-permission-status-revoked"
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                Revoked
              </span>
            ) : (
              <span data-testid="sms-permission-status-not-granted"
                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-50 text-slate-700 border border-slate-200">
                Not granted
              </span>
            )}
          </div>

          <div className="text-xs text-[var(--jc-text-soft)] mt-1">
            {phoneMasked ? (
              <>Phone on file: <span data-testid="sms-permission-phone-masked" className="font-mono">{phoneMasked}</span></>
            ) : state?.status === "phone_invalid_e164" ? (
              <>The stored phone couldn&apos;t be normalized. Update the customer phone above.</>
            ) : (
              <>No phone on file for this customer.</>
            )}
          </div>

          <ul className="mt-3 text-xs text-[var(--jc-text-soft)] space-y-1 list-disc pl-4">
            <li>Applies only to transactional project-document texts.</li>
            <li>Does <b>not</b> authorize marketing messages.</li>
            <li>Changing the customer phone requires permission to be recorded again.</li>
            <li>If the customer replies STOP, future SMS delivery is blocked.</li>
          </ul>

          {status === "opted_out" && (
            <p data-testid="sms-permission-opt-out-notice" className="mt-3 text-xs text-rose-700">
              The customer replied STOP. Contact them by other means (email or phone)
              before any further SMS delivery is authorized.
            </p>
          )}

          {status !== "granted" && phoneMasked && (
            <div className="mt-3 flex flex-col gap-2">
              <label className="text-xs flex items-start gap-2 text-[var(--jc-text-soft)]">
                <input
                  type="checkbox"
                  data-testid="sms-permission-affirmation-checkbox"
                  checked={affirmed}
                  onChange={(e) => setAffirmed(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I confirm the customer authorized transactional SMS delivery for
                  project documents to the number above.
                </span>
              </label>
              <button
                data-testid="sms-permission-grant-button"
                disabled={!affirmed || pending}
                onClick={onGrant}
                className="w-max text-xs px-3 py-1.5 rounded-md bg-[var(--jc-primary)] text-white disabled:opacity-50"
              >
                {pending ? "Saving…" : "Grant transactional SMS permission"}
              </button>
            </div>
          )}

          {status === "granted" && (
            <div className="mt-3">
              <button
                data-testid="sms-permission-revoke-button"
                disabled={pending}
                onClick={onRevoke}
                className="text-xs px-3 py-1.5 rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50"
              >
                {pending ? "Saving…" : "Revoke permission"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
